import { useState, useEffect } from 'react';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl';
import { BAD_DEPLOYMENTS_DATA_END_DATE } from '../../../common/constants';
import {
	isDateAfterToday,
	isDateBeforeApril,
	startOfDay,
	getDate,
	getRoundedDate,
} from '../../../common/utils/dates';
import { useDateFilter, getValidatedDateRange } from '../../../controllers/date-filter';
import messages from './messages';

type ValidationMessage = {
	type: 'VALID' | 'ERROR' | 'HELPER';
	message: MessageDescriptor;
};

export const getDateValidationMessage = (date: Date): ValidationMessage => {
	if (isDateAfterToday(date)) {
		return {
			type: 'ERROR',
			message: messages.futureDateErrorMessage,
		};
	}
	if (isDateBeforeApril(date)) {
		return {
			type: 'ERROR',
			message: messages.beforeAprilDateErrorMessage,
		};
	}
	return {
		type: 'VALID',
		message: messages.dateSuccessMessage,
	};
};

const getInvalidDateErrorMessage = (
	invalidDateType: 'EMPTY' | 'INVALID-FORMAT' | 'INVALID-DATE',
): ValidationMessage | undefined => {
	if (invalidDateType === 'INVALID-DATE') {
		return {
			type: 'ERROR',
			message: messages.invalidDateErrorMessage,
		};
	}
	if (invalidDateType === 'INVALID-FORMAT') {
		return {
			type: 'HELPER',
			message: messages.invalidDateFormatHelperMessage,
		};
	}
	return undefined;
};

type InvalidDateRangeState = 'MORE-THAN-YEAR-APART' | 'START-AFTER-END';

export const getDateRangeValidationMessage = (
	dateRangeState: InvalidDateRangeState,
): ValidationMessage | undefined => {
	switch (dateRangeState) {
		case 'MORE-THAN-YEAR-APART':
			return { type: 'ERROR', message: messages.dateRangeMoreThanYearApartErrorMessage };
		case 'START-AFTER-END':
			return { type: 'ERROR', message: messages.startDateAfterEndDateErrorMessage };
		default:
			// prettier-ignore
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			(dateRangeState as never);
			throw new Error('No date range validation enum found when setting start date');
	}
};
type UseDateRangePickerForm = {
	startDate?: Date;
	endDate?: Date;
	startValidation?: ValidationMessage;
	endValidation?: ValidationMessage;
	startDateInputChangeHandler: (value: string) => void;
	endDateInputChangeHandler: (value: string) => void;
	startCalendarSelectHandler: (date: Date) => void;
	endCalendarSelectHandler: (date: Date) => void;
	isFormValid: boolean;
};

export const useDateRangePickerForm = (): UseDateRangePickerForm => {
	const [dateFilter] = useDateFilter();
	const [startDate, setStartDate] = useState(
		dateFilter.type === 'CUSTOM' ? dateFilter.startDate : undefined,
	);
	const [endDate, setEndDate] = useState(
		dateFilter.type === 'CUSTOM' ? dateFilter.endDate : undefined,
	);
	const [startValidation, setStartValidation] = useState<unknown>();
	const [endValidation, setEndValidation] = useState<unknown>();
	const [isStartDateInputValid, setIsStartDateInputValid] = useState(dateFilter.type === 'CUSTOM');
	const [isEndDateInputValid, setIsEndDateInputValid] = useState(dateFilter.type === 'CUSTOM');

	const startDateInputChangeHandler = (value: string) => {
		const date = getDate(value);
		if (date instanceof Date) {
			setStartValidation(getDateValidationMessage(date));
			setIsStartDateInputValid(true);
			if (isEndDateInputValid) {
				setEndValidation({
					type: 'VALID',
					message: messages.dateSuccessMessage,
				});
			}
			return setStartDate(
				startOfDay(
					getRoundedDate(date, {
						disableFuture: true,
						minDate: BAD_DEPLOYMENTS_DATA_END_DATE,
					}),
				),
			);
		}
		setIsStartDateInputValid(false);
		return setStartValidation(getInvalidDateErrorMessage(date));
	};

	const endDateInputChangeHandler = (value: string) => {
		const date = getDate(value);
		if (date instanceof Date) {
			setEndValidation(getDateValidationMessage(date));
			setIsEndDateInputValid(true);
			if (isStartDateInputValid) {
				setStartValidation({
					type: 'VALID',
					message: messages.dateSuccessMessage,
				});
			}
			return setEndDate(
				getRoundedDate(date, {
					disableFuture: true,
					minDate: BAD_DEPLOYMENTS_DATA_END_DATE,
				}),
			);
		}
		setIsEndDateInputValid(false);
		return setEndValidation(getInvalidDateErrorMessage(date));
	};

	const startCalendarSelectHandler = (date: Date) => {
		setStartDate(date);
		setIsStartDateInputValid(true);
		setStartValidation({
			type: 'VALID',
			message: messages.dateSuccessMessage,
		});
		if (isEndDateInputValid) {
			setEndValidation({
				type: 'VALID',
				message: messages.dateSuccessMessage,
			});
		}
	};

	const endCalendarSelectHandler = (date: Date) => {
		setEndDate(date);
		setIsEndDateInputValid(true);
		setEndValidation({
			type: 'VALID',
			message: messages.dateSuccessMessage,
		});
		if (isStartDateInputValid) {
			setStartValidation({
				type: 'VALID',
				message: messages.dateSuccessMessage,
			});
		}
	};

	useEffect(() => {
		if (startDate && endDate) {
			const {
				startDate: validatedStartDate,
				endDate: validatedEndDate,
				errorType,
			} = getValidatedDateRange({ startDate, endDate });
			setStartDate(validatedStartDate);
			setEndDate(validatedEndDate);
			if (errorType) {
				setStartValidation(getDateRangeValidationMessage(errorType));
				setEndValidation(undefined);
			}
		}
	}, [endDate, startDate]);

	const isFormValid = Boolean(startDate && endDate && isStartDateInputValid && isEndDateInputValid);

	return {
		startDate,
		endDate,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'ValidationMessage | undefined'.
		startValidation,
		// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'ValidationMessage | undefined'.
		endValidation,
		startDateInputChangeHandler,
		endDateInputChangeHandler,
		startCalendarSelectHandler,
		endCalendarSelectHandler,
		isFormValid,
	};
};
